import { useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { isAppRelativePath, isNotificationHref, type NotificationItem } from '@packages/types';
import { flattenPages, queryKeys } from '@packages/api';
import { formatRelativeTime } from '@packages/format';
import { useAuth } from '../../lib/auth/useAuth';
import { track } from '../../lib/ui-usage';
import { cn } from '../../lib/utils';
import { BellIcon } from '../ui/BellIcon';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { Reveal } from '../ui/Reveal';
import { Skeleton, SkeletonRegion } from '../ui/Skeleton';
import { useRevealGroup } from '../../hooks/useRevealGroup';
import { Popover } from '../ui/Popover';
import { XIcon } from '../ui/XIcon';
import {
  useArchiveNotification,
  useClearReadNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotificationList,
  useUnreadNotificationCount,
} from './useNotifications';

const MAX_BADGE_COUNT = 99;

const quietActionClass =
  'text-xs text-text-muted transition-colors hover:text-secondary disabled:pointer-events-none disabled:opacity-50';

/** The bell and its popover of in-app notifications. */
export function NotificationBell() {
  const { accountId } = useAuth();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: unreadData } = useUnreadNotificationCount(accountId ?? '');
  const unreadCount = unreadData?.count ?? 0;
  const { data: listData, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useNotificationList(
    accountId ?? '',
    open,
  );
  const items = listData ? flattenPages(listData.pages) : [];
  const markRead = useMarkNotificationRead(accountId ?? '');
  const markAllRead = useMarkAllNotificationsRead(accountId ?? '');
  const clearRead = useClearReadNotifications(accountId ?? '');
  const archive = useArchiveNotification(accountId ?? '');

  if (!accountId) return null;

  const hasUnreadItems = items.some((item) => item.readAt === null);
  const badgeLabel = unreadCount > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : String(unreadCount);

  // The count is polled and the list keeps its normal staleTime, so a reopen can
  // show cached rows. Refreshing the count on open keeps the badge current, and
  // "Mark all as read" also keys off the loaded rows so the two cannot strand it.
  const handleToggle = () => {
    if (!open) {
      track('ui-usage.notifications.opened', { unread: unreadCount > 0 ? 'some' : 'none' });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notificationUnreadCount(accountId) });
    }
    setOpen(!open);
  };

  const handleItemClick = (item: NotificationItem) => {
    if (item.readAt === null) markRead.mutate(item);
    setOpen(false);
  };

  const handleClear = (item: NotificationItem) => {
    archive.mutate(item);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-bg-muted hover:text-secondary lg:h-8 lg:w-8"
      >
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            aria-hidden="true"
            className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-white tabular-nums"
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <Popover
          triggerRef={triggerRef}
          onClose={() => setOpen(false)}
          ariaLabel="Notifications"
          widthPx={384}
          widthClass="w-96"
          bodyClassName="p-0"
          maxHeightCap="66dvh"
          header={
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-sans text-sm font-medium text-secondary">Notifications</h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className={quietActionClass}
                  disabled={(unreadCount === 0 && !hasUnreadItems) || markAllRead.isPending}
                  onClick={() => markAllRead.mutate()}
                >
                  Mark all as read
                </button>
                <button
                  type="button"
                  className={quietActionClass}
                  disabled={items.length === 0 || clearRead.isPending}
                  onClick={() => clearRead.mutate()}
                >
                  Clear read
                </button>
              </div>
            </div>
          }
        >
          <NotificationListReveal isLoading={isLoading}>
          {isError ? (
            <p role="alert" className="px-4 py-6 text-sm text-text-muted">
              Couldn&apos;t load notifications.
            </p>
          ) : items.length === 0 ? (
            <EmptyState variant="section" title="No notifications" className="px-4" />
          ) : (
            <>
              <ul className="divide-y divide-border">
                {items.map((item) => (
                  <li key={item.id} className="group relative">
                    <NotificationRow item={item} onSelect={handleItemClick} onClear={handleClear} />
                  </li>
                ))}
              </ul>
              {hasNextPage && (
                <div className="border-t border-border p-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    loading={isFetchingNextPage}
                    onClick={() => void fetchNextPage()}
                  >
                    Load more
                  </Button>
                </div>
              )}
            </>
          )}
          </NotificationListReveal>
          <div className="border-t border-border px-4 py-2 text-right">
            <Link
              to="/settings?tab=notifications"
              onClick={() => setOpen(false)}
              className={cn(quietActionClass, 'no-underline')}
            >
              Notification settings
            </Link>
          </div>
        </Popover>
      )}
    </>
  );
}

/**
 * Owns the reveal group so it starts when the popover opens: the list query is
 * disabled while closed, and a group created earlier would already be latched.
 */
function NotificationListReveal({ isLoading, children }: { isLoading: boolean; children: ReactNode }) {
  const listRead = { isLoading };
  const reveal = useRevealGroup([listRead]);
  return (
    <Reveal group={reveal} read={listRead} placeholder={<NotificationsSkeleton />}>
      {children}
    </Reveal>
  );
}

/** Notification rows (`px-4 py-3`, a title and time over a body line) at their loaded size. */
function NotificationsSkeleton() {
  return (
    <SkeletonRegion label="Loading notifications" className="divide-y divide-border">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="px-4 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-10" />
          </div>
          <Skeleton className="mt-1.5 h-3 w-4/5" />
        </div>
      ))}
    </SkeletonRegion>
  );
}

function NotificationRow({
  item,
  onSelect,
  onClear,
}: {
  item: NotificationItem;
  onSelect: (item: NotificationItem) => void;
  onClear: (item: NotificationItem) => void;
}) {
  const unread = item.readAt === null;
  const href = item.href !== null && isNotificationHref(item.href) ? item.href : null;
  const isExternal = href !== null && !isAppRelativePath(href);
  const rowClass = cn(
    'flex w-full gap-3 px-4 py-3 text-left no-underline transition-colors hover:bg-bg-muted',
    unread && 'bg-primary/5',
  );
  const content = (
    <>
      <span
        aria-hidden="true"
        className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', unread ? 'bg-primary' : 'bg-transparent')}
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-3">
          <span className={cn('text-[13px] text-secondary', unread ? 'font-medium' : 'font-normal')}>{item.title}</span>
          <time
            dateTime={item.createdAt}
            // Where hover exists the clear button takes the time's spot; on touch both show.
            className="shrink-0 text-xs tabular-nums text-text-muted group-focus-within:invisible group-hover:invisible [@media(hover:none)]:mr-7"
          >
            {formatRelativeTime(item.createdAt)}
          </time>
        </span>
        {/* pre-line keeps the blank lines an operator puts between paragraphs of an announcement. */}
        <span className="mt-0.5 block whitespace-pre-line break-words text-xs text-text-muted">{item.body}</span>
        {unread && <span className="sr-only">Unread</span>}
      </span>
    </>
  );

  return (
    <>
      {href === null ? (
        <button type="button" onClick={() => onSelect(item)} className={rowClass}>
          {content}
        </button>
      ) : isExternal ? (
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={() => onSelect(item)} className={rowClass}>
          {content}
        </a>
      ) : (
        <Link to={href} onClick={() => onSelect(item)} className={rowClass}>
          {content}
        </Link>
      )}
      <button
        type="button"
        aria-label="Clear notification"
        onClick={() => onClear(item)}
        className="absolute right-3 top-2.5 flex h-6 w-6 items-center justify-center rounded text-text-muted opacity-0 transition-colors hover:bg-bg-muted hover:text-secondary focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
      >
        <XIcon className="h-4 w-4" />
      </button>
    </>
  );
}
